<script setup>
  import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
  import { useLayout } from '@/layout/composables/layout';
  import { useRouter } from 'vue-router';
  import AppConfigurator from './AppConfigurator.vue';

  import { useAuthStore } from '@/stores/auth';
  import { useLayoutStore } from '@/stores/layout';
  import { useChatStore } from '@/stores/chat';
  import AutoLogoutTimer from '@/components/AutoLogoutTimer.vue';
  import { isHeroDomain, getHeroLogoPath } from '@/utils/domainMapping';

  const { layoutConfig, onMenuToggle, toggleDarkMode, isDarkTheme } =
    useLayout();

  const outsideClickListener = ref(null);
  const topbarMenuActive = ref(false);
  const router = useRouter();
  const isLoading = ref(false);
  const authStore = useAuthStore();

  const layoutStore = useLayoutStore();
  const chatStore = useChatStore();
  const heroDomain = isHeroDomain();
  const heroLogoPath = getHeroLogoPath();

  const menu = ref();
  const items = ref([
    {
      label: 'My Page',
      icon: 'pi pi-cog',
      command: () => {
        router.push('/user');
      },
    },
    {
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => {
        handleLogout();
      },
    },
  ]);

  const toggle = (event) => {
    menu.value.toggle(event);
  };

  const menuSet = ref();
  const itemSet = ref([
    {
      label: 'Top Menu',
      icon: 'pi pi-list',
      command: () => {
        changeLayout('top');
      },
    },
    {
      label: 'Side Menu',
      icon: 'pi pi-list',
      command: () => {
        changeLayout('sidebar');
      },
    },
    // {
    //   label: 'Tabs',
    //   icon: 'pi pi-folder',
    //   command: () => {
    //     layoutStore.toggleTabView();
    //   },
    // },
    // {
    //   label: 'BreadC',
    //   icon: 'pi pi-share-alt',
    //   command: () => {
    //     layoutStore.toggleBreadcrumb();
    //   },
    // },
    {
      label: 'Chat',
      icon: 'pi pi-comments',
      command: () => {
        // 현재 채팅 상태 확인
        const currentChatState = layoutStore.useChat;
        
        // 채팅 토글
        layoutStore.toggleChat();
        
        // 채팅이 활성화되면 채팅 창 열기
        if (layoutStore.useChat) {
          chatStore.isOpen = true;
        } else {
          // 채팅이 비활성화되면 채팅 창 닫기
          chatStore.isOpen = false;
        }
      },
    },
    {
      label: 'Full Screen',
      icon: 'pi pi-window-maximize',
      command: () => {
        layoutStore.toggleFullScreen();
      },
    },
  ]);

  const toggleSet = (event) => {
    menuSet.value.toggle(event);
  };

  // 채팅 토글 핸들러 추가
  const handleChatToggle = () => {
    // 현재 채팅 상태 확인
    const currentChatState = layoutStore.useChat;
    
    // 채팅 토글
    layoutStore.toggleChat();
    
    // 채팅이 활성화되면 채팅 창 열기
    if (layoutStore.useChat) {
      chatStore.isOpen = true;
    } else {
      // 채팅이 비활성화되면 채팅 창 닫기
      chatStore.isOpen = false;
    }
  };

  // 레이아웃 변경 함수 추가
  const changeLayout = (mode) => {
    console.log('Changing layout to:', mode); // 디버깅용
    layoutStore.setLayoutMode(mode);
  };

  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  // 회사명으로 폰트 크기 계산 함수
  const getCompanyLogoFontSize = (companyName) => {
    const maxHeight = 32; // 최대 높이 (로고 영역 높이)
    const baseFontSize = Math.min(26, Math.floor(maxHeight * 0.9)); // 기본 폰트 크기를 1.5배 증가 (16 -> 24)
    
    // 텍스트가 너무 길면 폰트 크기 줄이기
    const textLength = companyName.length;
    if (textLength > 10) {
      return Math.max(16, Math.floor(baseFontSize * (10 / textLength))); // 최소 크기도 1.5배 증가 (10 -> 15)
    }
    
    return baseFontSize;
  };

  const logoExists = ref(false);
  const darkLogoExists = ref(false);
  const lightLogoExists = ref(false);

  const isDevMode = import.meta.env.MODE === 'development';

  onMounted(async () => {
    //bindOutsideClickListener();

    if (authStore.isAuthenticated && !authStore.user) {
      try {
        await authStore.fetchUserInfo();
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      }
    }
    
    // hero 도메인 강제 설정
    if (heroDomain) {
      authStore.tenantId = '2';
      lightLogoExists.value = true;
      darkLogoExists.value = true;
      logoExists.value = true;
    } else {
      // 기본 로고와 다크모드 로고 존재 여부 모두 확인
      const lightLogoPath = `/layout/images/logo/logo-${authStore.comId}.png`;
      const darkLogoPath = `/layout/images/logo/logo-${authStore.comId}-dark.png`;
      
      lightLogoExists.value = await checkImageExists(lightLogoPath);
      darkLogoExists.value = await checkImageExists(darkLogoPath);
      
      // 현재 테마에 맞는 로고 존재 여부 설정
      logoExists.value = isDarkTheme.value ? darkLogoExists.value : lightLogoExists.value;
    }
  });

  onBeforeUnmount(() => {
    //unbindOutsideClickListener();
  });

  // 다크모드 변경 시 로고 확인
  watch(isDarkTheme, async (newValue) => {
    // 현재 테마에 맞는 로고 존재 여부 설정
    logoExists.value = newValue ? darkLogoExists.value : lightLogoExists.value;
  });

  // const logoUrl = computed(() => {
  //     return `/layout/images/${layoutConfig.darkTheme.value ? 'logo-white' : 'logo-dark'}.svg`;
  // });

  const logoUrl = computed(() => {
    return `/layout/images/${layoutConfig.darkTheme.value ? 'd2s_logo' : 'd2s_logo'}.png`;
  });

  // 다크모드 이미지가 없을 때 CSS 필터 적용을 위한 computed
  const logoImageClass = computed(() => {
    if (!logoExists.value && isDarkTheme.value) {
      return 'dark-mode-adjustable';
    }
    return '';
  });

  // 로고 이미지 경로 계산
  const logoImageSrc = computed(() => {
    if (heroDomain) {
      return heroLogoPath;
    }
    if (isDarkTheme.value) {
      // 다크모드일 때
      if (darkLogoExists.value) {
        // 다크모드 이미지가 있으면 사용
        return `/layout/images/logo/logo-${authStore.user?.company_id || ''}-dark.png`;
      } else if (lightLogoExists.value) {
        // 다크모드 이미지가 없고 기본 이미지가 있으면 기본 이미지 사용 (필터 적용)
        return `/layout/images/logo/logo-${authStore.user?.company_id || ''}.png`;
      }
    } else {
      // 화이트모드일 때
      if (lightLogoExists.value) {
        // 기본 이미지가 있으면 사용
        return `/layout/images/logo/logo-${authStore.user?.company_id || ''}.png`;
      }
    }
    // 이미지가 없으면 빈 문자열 반환
    return '';
  });

  const shouldShowLogoImage = computed(() => {
    if (heroDomain) return true;
    return logoImageSrc.value && authStore.user?.company_id;
  });

  const onTopBarMenuButton = () => {
    topbarMenuActive.value = !topbarMenuActive.value;
  };
  const onSettingsClick = () => {
    topbarMenuActive.value = false;
    router.push('/documentation');
  };
  const topbarMenuClasses = computed(() => {
    return {
      'layout-topbar-menu-mobile-active': topbarMenuActive.value,
    };
  });

  const bindOutsideClickListener = () => {
    if (!outsideClickListener.value) {
      outsideClickListener.value = (event) => {
        if (isOutsideClicked(event)) {
          topbarMenuActive.value = false;
        }
      };
      document.addEventListener('click', outsideClickListener.value);
    }
  };
  const unbindOutsideClickListener = () => {
    if (outsideClickListener.value) {
      document.removeEventListener('click', outsideClickListener);
      outsideClickListener.value = null;
    }
  };
  const isOutsideClicked = (event) => {
    if (!topbarMenuActive.value) return;

    const sidebarEl = document.querySelector('.layout-topbar-menu');
    const topbarEl = document.querySelector('.layout-topbar-menu-button');

    return !(
      sidebarEl.isSameNode(event.target) ||
      sidebarEl.contains(event.target) ||
      topbarEl.isSameNode(event.target) ||
      topbarEl.contains(event.target)
    );
  };

  const handleLogout = async () => {
    isLoading.value = true;
    try {
      await authStore.logout();
      router.push('/auth/login'); // 로그인 페이지로 리다이렉트
    } catch (error) {
      console.error('Logout failed:', error);
      // 에러 처리 (예: 사용자에게 알림)
    } finally {
      isLoading.value = false;
    }
  };

  // 로고 클릭 핸들러 추가
  const handleLogoClick = () => {
    // 벤더 사용자인 경우 벤더 대시보드로 이동
    if (authStore.isVendorUser) {
      router.push('/vendor/dashboard');
    } else {
      // 일반 사용자인 경우 기본 대시보드로 이동
      router.push('/');
    }
  };
</script>

<template>
  <div class="layout-topbar">
    <!-- 모바일 메뉴버튼 -->
    <button ref="menuButton"
      v-if="layoutStore.layoutMode === 'sidebar'"
      class="p-link layout-menu-button layout-topbar-button menu-mobile"
      @click="onMenuToggle()"
    >
      <i class="pi pi-bars"></i>
    </button>
    <div ref="logoLink" class="layout-topbar-logo" @click="handleLogoClick" @touchstart="handleLogoClick" style="cursor: pointer;">
      <img
        class="max-w-[120px] max-h-[40px] object-contain"
        v-if="shouldShowLogoImage"
        :src="logoImageSrc"
        alt="logo"
        style="height: 36px"
        :class="logoImageClass"
      />
      <span
        v-else
        class="company-logo-text"
        :style="{
          fontSize: `${getCompanyLogoFontSize(authStore.company_name || 'AssetCare')}px`,
          color: isDarkTheme ? '#ffffff' : '#000000'
        }"
      >
        {{ authStore.company_name }}
      </span>
      <span class="assetcare-logo-text">AssetCare</span>
    </div>
    <div ref="avatarContainer-mobile" class="avatar-container avatar-mobile">
      <OverlayBadge
        value="0"
        severity="danger"
        class="inline-flex"
      >
        <Avatar
          icon="pi pi-user"
          @click="toggle"
          aria-haspopup="true"
          aria-controls="overlay_menu"
          style="cursor: pointer"
        />
        <Menu
          ref="menu"
          id="overlay_menu"
          :model="items"
          :popup="true"
        />
      </OverlayBadge>
    </div>
    <div class="layout-topbar-menu" :class="topbarMenuClasses">
      <span v-if="isDevMode && false" ref="userInfo" class="user-id"
        >{{ authStore.userEmail }} ({{ authStore.userName }}님 :
        {{ authStore.comName }}-{{ authStore.comId }}-{{
          authStore.vendorName
        }}-{{ authStore.vendorId }}){{ authStore.role }}:{{
          authStore.permission
        }}:{{ authStore.vendorRole }}</span
      >
      <!--<AutoLogoutTimer />-->
      <!-- 레이아웃 전환 버튼 -->
      <div class="layout-mode-buttons"></div>
      <button
        type="button"
        class="p-link layout-topbar-button"
        @click="toggleDarkMode"
      >
        <i
          :class="['pi', { 'pi-moon': isDarkTheme, 'pi-sun': !isDarkTheme }]"
        ></i>
        <span>화면모드</span>
      </button>

      <div class="relative">
        <button
          v-styleclass="{
            selector: '@next',
            enterFromClass: 'hidden',
            enterActiveClass: 'animate-scalein',
            leaveToClass: 'hidden',
            leaveActiveClass: 'animate-fadeout',
            hideOnOutsideClick: true,
          }"
          type="button"
          class="p-link layout-topbar-button"
        >
          <i class="pi pi-palette"></i>
        </button>
        <AppConfigurator />
      </div>
      <button
        type="button"
        class="p-link layout-topbar-button"
        @click="toggleSet"
      >
        <i class="pi pi-cog"></i>
        <span>테마설정</span>
      </button>
      <button
        type="button"
        class="p-link layout-topbar-button"
        @click="handleChatToggle()"
      >
        <i class="pi pi-comments"></i>
        <span>채팅</span>
      </button>
      <button
        type="button"
        class="p-link layout-topbar-button"
        @click="layoutStore.toggleFullScreen()"
      >
        <i class="pi pi-window-maximize"></i>
        <span>전체화면</span>
      </button>
      <Menu
        ref="menuSet"
        id="overlay_menuSet"
        :model="itemSet"
        :popup="true"
      />
      <div class="separator"></div>
      <!-- PC에서만 보이는 아바타 -->
      <div class="avatar-container avatar-pc">
        <OverlayBadge
          value="0"
          severity="danger"
          class="inline-flex"
        >
          <Avatar
            icon="pi pi-user"
            @click="toggle"
            aria-haspopup="true"
            aria-controls="overlay_menu"
            style="cursor: pointer"
          />
          <Menu
            ref="menu"
            id="overlay_menu"
            :model="items"
            :popup="true"
          />
        </OverlayBadge>
      </div>
      <!--
      <button @click="handleLogout" :disabled="isLoading">
        {{ isLoading ? "Logging out..." : "Logout" }}
      </button>
      -->
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .layout-topbar {
    display: flex;
    align-items: center;
    padding: 0 2rem;
    height: 4rem;
    background-color: var(--surface-card);
    border-bottom: 1px solid var(--surface-border);
    z-index: 997;
    overflow-x: auto;

    /* 다크 모드에서 이미지에 적용할 필터 */
    .dark-mode-adjustable {
      filter: invert(1) hue-rotate(180deg) brightness(0.8) contrast(1.2);
      mix-blend-mode: lighten;
    }

    .layout-topbar-logo {
      display: flex;
      align-items: center;
      min-width: 0;
      height: 32px;

      img {
        display: flex;
        align-items: center;
        height: 32px;
      }
      .company-logo-text,
      .assetcare-logo-text {
        display: flex;
        align-items: center;
        height: 32px;
      }
      .company-logo-text {
        font-weight: bold;
        font-family: Arial, sans-serif;
        flex-shrink: 0;
        min-width: 0;
        margin-right: 0.5rem;
        color: #000;
        /* 윤곽선 효과 */
        text-shadow:
          0 0 1px #aaa,
          0 0 2px #aaa,
          1px 1px 2px #aaa;
        transition: color 0.3s, text-shadow 0.3s;
      }
      .dark .company-logo-text {
        color: #fff;
        text-shadow:
          0 0 1px #7f7f7f,
          0 0 2px #7f7f7f,
          1px 1px 2px #7f7f7f;
      }
      .assetcare-logo-text {
        font-weight: bold;
        white-space: nowrap;
        flex-shrink: 0;
      }
    }

    .layout-topbar-menu {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: auto;
      gap: 0.25rem;

      .user-id {
        margin-right: 1rem;
      }
    }

    .layout-mode-buttons {
      display: flex;
      gap: 0.5rem;

      .layout-mode-btn {
        display: flex;
        align-items: center;
        padding: 0.5rem 1rem;

        &.active {
          background-color: var(--primary-color);
          color: var(--primary-color-text);
        }
      }
    }

    .logout-button {
      padding: 0.5rem 1rem;
      margin-left: 1rem;
    }

    .layout-topbar-button {
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      border-radius: 4px;
      cursor: pointer;
      transition: background-color 0.2s;

      &:hover {
        background-color: var(--surface-hover);
      }

      i {
        font-size: 1.25rem;
      }
    }

    .separator {
      width: 1px;
      height: 24px;
      background-color: var(--surface-border);
      margin: 0 0.75rem;
    }

    .avatar-container {
      display: flex;
      align-items: center;
    }
  }
  .layout-topbar .layout-menu-button {
    margin-right: 0.5rem; /* 버튼의 오른쪽 여백 추가 */
  }
  .avatar-mobile {
    display: none !important;
  }
  .avatar-pc {
    display: flex !important;
  }

  @media screen and (max-width: 960px) {
    .avatar-mobile {
      display: flex !important;
      align-items: center;
      margin-left: 0.5rem;
      order: 3;
    }
    .avatar-pc {
      display: none !important;
    }
    .layout-topbar {
      flex-direction: row;
      align-items: center;
    }
    .layout-topbar-logo {
      order: 1;
      margin-right: 0;
    }
    .menu-mobile {
      order: 2;
      margin-left: auto;
    }
    .layout-topbar-menu {
      display: none !important;
    }
  }
</style>
